import React, { useEffect, useState } from 'react';
import styles from './Filter.module.scss';

import FilterWrapper from 'components/FilterComponents/FilterWrapper';
import { FormLabel } from 'components/FormComponents';
import FormAutoMultiSelect from 'components/FormComponents/FormAutoMultiSelect/FormAutoMultiSelect';
import FormMultiSelect from 'components/FormComponents/FormMultiSelect/FormMultiSelect';

import FilterRadios from '../../FilterBlocks/Radios';

import { useTicketsDashboardActions, useTicketsDashboardSelector } from 'hooks/TicketsDashboard';

import { FormProvider, useForm } from 'react-hook-form';

import { enrichNameToId, isSuccessfulStatus } from 'helpers/AppHelpers';

import { generateChips } from './helpers';
import { radios } from '../../../helpers';

export default function FlowStatisticsFilter({ filter }) {
  const methods = useForm({
    defaultValues: {
      assets: [],
      locations: [],
      prefixes: [],
      rootCauses: [],
      ticketTypes: [],
      severities: [],
      openedObj: {},
      opened: true
    },
    mode: 'onChange'
  });
  const { getValues, reset, setValue, watch, formState } = methods;
  const { errors } = formState;
  const isFormInvalid = !!Object.values(errors).filter(Boolean).length;

  const [open, setOpen] = useState(false);
  const [chips, setChips] = useState([]);
  const [assetOptions, setAssetOptions] = useState([]);

  const { filterCriteria } = useTicketsDashboardSelector();
  const {
    getDashboardConfigAction,
    updateSectionConfigAction,
    getFlowStatisticsAction,
    getAssetOptionsAction
  } = useTicketsDashboardActions();

  const prepareFilterValues = (data) => ({
    ...data,
    severities: data?.severities?.length ? enrichNameToId(data.severities) : [],
    openedObj: radios[data.opened ? 0 : 1]
  });

  const getAssetOptions = (prefixIds) =>
    getAssetOptionsAction({ filters: { prefixIds } }).then((res) =>
      setAssetOptions(res?.items || [])
    );

  useEffect(() => {
    if (open) {
      filter?.prefixes?.length && getAssetOptions(filter.prefixes.map(({ id }) => id));
      reset(prepareFilterValues(filter));
    }
  }, [open]);

  useEffect(() => {
    if (filterCriteria?.['locations']?.length) {
      reset(prepareFilterValues(filter));
      createChips(filter);
    }
  }, [filter, filterCriteria]);

  const { locations, prefixes, rootCauses, ticketTypes, severities } = filterCriteria;

  const clearFilter = () => {
    const defaultState = {
      ...getValues(),
      assets: [],
      locations: [],
      prefixes: [],
      rootCauses: [],
      ticketTypes: [],
      severities: [],
      openedObj: radios[0],
      opened: true
    };
    reset(defaultState);
  };

  const closeFilter = () => setOpen(false);
  const toggleFilter = (value) => setOpen(value || !open);

  const applyFilter = async () => {
    const values = getValues();

    const response = await updateSectionConfigAction(values);

    if (!isSuccessfulStatus(response?.status)) return;

    closeFilter();

    getDashboardConfigAction({ dashboardType: values.dashboardType }).then(() => {
      getFlowStatisticsAction();
    });
  };

  const createChips = (data) => setChips(generateChips(data));

  const refreshAssetsValue = () => {
    if (!getValues('assets')?.length) return;

    if (!getValues('prefixes').length) {
      setValue('assets', []);
      return;
    }

    const prefixIds = getValues('prefixes').map(({ id }) => id);
    const filteredAssets = getValues('assets').filter(({ assetPrefix }) =>
      prefixIds.includes(assetPrefix.id)
    );

    setValue('assets', filteredAssets);
  };

  const handlePrefixSelect = (_, value) => {
    refreshAssetsValue();

    if (!value?.length) return;

    getAssetOptions(value?.map(({ id }) => id) || []);
  };

  const handleMultiSelect = (name, value) => setValue(name, value);

  const prefixesWatcher = watch('prefixes');

  return (
    <FilterWrapper
      open={open}
      transparentBackDrop={false}
      disableChipDelete
      onClick={toggleFilter}
      onApply={applyFilter}
      onCancel={closeFilter}
      onClear={clearFilter}
      onBack={closeFilter}
      isApplyDisabled={isFormInvalid}
      chips={chips}>
      <FormProvider {...methods}>
        <div className={styles.wrapper}>
          <div className={styles.block}>
            <div className={styles.block__cell}>
              <FormLabel>Location</FormLabel>
              <FormAutoMultiSelect
                name="locations"
                menuItems={locations || []}
                options={{
                  label: 'siteCode',
                  disableByObjectTracker: false,
                  disableLabel: true
                }}
              />
            </div>
            <div className={styles.block__cell}>
              <FormLabel>Prefix</FormLabel>
              <FormAutoMultiSelect
                name="prefixes"
                menuItems={prefixes || []}
                options={{
                  label: 'prefix',
                  disableByObjectTracker: false,
                  disableLabel: true,
                  extraAction: handlePrefixSelect
                }}
              />
            </div>
            <div className={styles.block__cell}>
              <FormLabel>Asset</FormLabel>
              <FormAutoMultiSelect
                name="assets"
                menuItems={assetOptions || []}
                options={{
                  label: 'drCode',
                  disableByObjectTracker: false,
                  disableLabel: true,
                  isDisabled: !prefixesWatcher?.length
                }}
              />
            </div>
          </div>
          <div className={styles.block}>
            <div className={styles.block__cell}>
              <FormLabel>Ticket Type</FormLabel>
              <FormAutoMultiSelect
                name="ticketTypes"
                menuItems={ticketTypes || []}
                options={{
                  label: 'name',
                  disableByObjectTracker: false,
                  disableLabel: true
                }}
              />
            </div>
            <div className={styles.block__cell}>
              <FormLabel>Root Causes</FormLabel>
              <FormAutoMultiSelect
                name="rootCauses"
                menuItems={rootCauses || []}
                options={{
                  label: 'name',
                  disableByObjectTracker: false,
                  disableLabel: true
                }}
              />
            </div>
            <div className={styles.block__cell}>
              <FormLabel>Severity</FormLabel>
              <FormMultiSelect
                name="severities"
                options={enrichNameToId(severities) || []}
                onSelect={handleMultiSelect}
              />
            </div>
            <FilterRadios />
          </div>
        </div>
      </FormProvider>
    </FilterWrapper>
  );
}
